import React from 'react';
import { H1, P } from '../../components/elements/Text';
import Navbar from '../../components/Navbar';
import PageWithAsyncData from '../../components/PageWithAsyncData';
import PageWrapper from '../../components/PageWrapper';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { useGetCompany } from '../../hooks/use-hydra/admin';
import {
  createColumnHelper,
  useReactTable,
  getCoreRowModel,
  flexRender,
  RowSelectionState,
  OnChangeFn,
} from '@tanstack/react-table';
import { CompanyResponse } from '../../api/admin';
import {
  TableHead,
  TableHeader,
  TableRow,
  TableBody,
  TableCell,
  ClickableTableRow,
  DateTableCellContent,
} from '../../components/Table';
import { Table } from '../../components/Table';
import { TableWrapper } from '../../components/Table';
import { toast } from 'react-toastify';
import Button from '../../components/elements/Button';
import day from 'dayjs';
import utc from 'dayjs/plugin/utc';
import Checkbox from '../../components/elements/Checkbox';
import { SmallTooltip } from '../../components/Tooltip';
import LearningPlanCell from '../../components/Admin/Cells/LearningPlanCell';
import EditLearnersLearningPlanModal from '../../components/Admin/Modals/EditLearnersLearningPlanModal';
import AddLearnersToCompanyModal from '../../components/Admin/Modals/AddLearnersToCompanyModal';
import InviteLearnersToCohortModal from '../../components/Admin/Modals/InviteLearnersToCohortModal';
day.extend(utc);

const Header = ({ name, id }: { name?: string; id?: string }) => (
  <Navbar
    breadCrumbs={[
      {
        label: 'Home',
        url: '/',
      },
      {
        label: 'Companies',
        url: '/admin/companies',
      },
      ...(name && id ? [{ label: name, url: `/admin/companies/${id}` }] : []),
      { label: 'Learners', url: null },
    ]}
  />
);

const Content = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0px;
  margin-bottom: 72px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
`;

export default function Learners() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error, refetch } = useGetCompany(parseInt(id));
  const [isInviteToCohortModalOpen, setIsInviteToCohortModalOpen] =
    React.useState(false);
  const [isEditLearningPlanModalOpen, setIsEditLearningPlanModalOpen] =
    React.useState(false);
  const [isAddLearnersModalOpen, setIsAddLearnersModalOpen] =
    React.useState(false);

  const [tableSelection, setTableSelection] = React.useState<
    Record<string, boolean>
  >({});

  const selectedLearners = React.useMemo(() => {
    return Object.keys(tableSelection).map((key) =>
      data?.learners.find((learner) => learner.id === parseInt(key))
    );
  }, [tableSelection, data]);

  const numSelected = Object.keys(tableSelection)
    .map((key) => tableSelection[key])
    .filter(Boolean).length;

  return (
    <PageWithAsyncData isLoading={isLoading} error={error} retry={refetch}>
      <Header name={data?.name} id={id} />
      <PageWrapper>
        <H1>{data?.name} Learners</H1>
        <Content>
          <Row>
            <Button
              label="Add New Learners"
              type="secondary"
              faIcon="fa-solid fa-plus"
              showIconBeforeLabel
              tooltip="Upload a CSV or add new learners manually."
              onClick={() => setIsAddLearnersModalOpen(true)}
            />
            <Button
              label="Invite to Cohort / Module"
              type="secondary"
              faIcon="fa-solid fa-calendar-circle-user"
              showIconBeforeLabel
              tooltip="Select one or more learners and invite them to a module, within a cohort."
              isDisabled={numSelected === 0}
              onClick={() => setIsInviteToCohortModalOpen(true)}
            />
            <Button
              label="Edit Learning Plan"
              type="secondary"
              faIcon="fa-solid fa-pen-to-square"
              showIconBeforeLabel
              tooltip="Select one or more learners and edit their learning plan."
              isDisabled={numSelected === 0}
              onClick={() => setIsEditLearningPlanModalOpen(true)}
            />
          </Row>
          <P>
            There are {data?.learners.length} learners in this company.
            {numSelected > 0 && <> {numSelected} learners selected.</>}
          </P>
          <LearnersTable
            data={data?.learners ?? []}
            learnerSelection={tableSelection}
            setLearnerSelection={setTableSelection}
          />
        </Content>
      </PageWrapper>
      <InviteLearnersToCohortModal
        isOpen={isInviteToCohortModalOpen}
        onClose={() => setIsInviteToCohortModalOpen(false)}
        companyId={parseInt(id)}
        selectedLearnerIds={selectedLearners.map(
          (learner) => learner?.id ?? -1
        )}
      />
      <EditLearnersLearningPlanModal
        isOpen={isEditLearningPlanModalOpen}
        onClose={() => setIsEditLearningPlanModalOpen(false)}
        companyId={parseInt(id)}
        selectedLearnerIds={selectedLearners.map(
          (learner) => learner?.id ?? -1
        )}
      />
      <AddLearnersToCompanyModal
        isOpen={isAddLearnersModalOpen}
        onClose={() => setIsAddLearnersModalOpen(false)}
        companyId={parseInt(id)}
      />
    </PageWithAsyncData>
  );
}

const columnHelper = createColumnHelper<CompanyResponse['learners'][number]>();

const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => (
      <Checkbox
        isChecked={table.getIsAllRowsSelected()}
        isIndeterminate={table.getIsSomeRowsSelected()}
        onChange={table.getToggleAllRowsSelectedHandler()}
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        isChecked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
      />
    ),
    meta: {
      isCentered: true,
      styles: {
        width: '40px',
      },
    },
  }),
  columnHelper.accessor('id', {
    header: 'ID',
    meta: {
      styles: {
        width: '40px',
      },
    },
  }),
  columnHelper.display({
    id: 'name',
    header: 'Name',
    cell: ({ row }) => {
      return (
        <>
          <div className={`learner-${row.original.id}`}>
            {row.original.name ?? 'N/A'}
          </div>
          <SmallTooltip anchorSelect={`.learner-${row.original.id}`}>
            {row.original.email}
          </SmallTooltip>
        </>
      );
    },
    meta: {
      styles: {
        minWidth: '150px',
        width: '100%',
      },
    },
  }),
  columnHelper.display({
    id: 'learning-plan',
    header: 'Learning Plan',
    cell: ({ row }) => (
      <LearningPlanCell
        learnerId={row.original.id}
        learningPlan={row.original.learningPlan}
      />
    ),
    meta: {
      styles: {
        display: 'flex',
        minWidth: '500px',
        maxWidth: '900px',
        overflowX: 'auto',
        scrollbarWidth: 'none',
      },
    },
  }),
  columnHelper.accessor('addedAt', {
    header: 'Added At',
    cell: (props) => <DateTableCellContent date={new Date(props.getValue())} />,
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
  columnHelper.accessor('lastActiveAt', {
    header: 'Last Active At',
    cell: (props) => <DateTableCellContent date={props.getValue()} />,
    meta: {
      styles: {
        minWidth: '180px',
      },
    },
  }),
];

const Wrapper = styled(TableWrapper)`
  overflow-x: auto;

  table {
    min-width: 1200px;
  }
`;

const LearnersTable = ({
  data,
  learnerSelection,
  setLearnerSelection,
}: {
  data: CompanyResponse['learners'];
  learnerSelection: Record<string, boolean>;
  setLearnerSelection: OnChangeFn<RowSelectionState>;
}) => {
  const table = useReactTable({
    data,
    columns,
    state: {
      rowSelection: learnerSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    onRowSelectionChange: setLearnerSelection,
    getRowId: (row) => row.id.toString(),
  });
  return (
    <Wrapper>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead meta={header.column.columnDef.meta} key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows.map((row) => (
            <ClickableTableRow
              isSelected={row.getIsSelected()}
              key={row.id}
              onClick={() => {
                toast.info('Learner profiles not implemented.');
              }}
            >
              {row.getVisibleCells().map((cell) => (
                <TableCell
                  meta={cell.column.columnDef.meta}
                  onClick={
                    cell.column.id === 'select'
                      ? (e) => e.stopPropagation() // prevent the checkbox from firing the row click handler
                      : undefined
                  }
                  style={{
                    cursor: cell.column.id === 'select' ? 'default' : 'pointer',
                  }}
                  key={cell.id}
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </ClickableTableRow>
          ))}
        </TableBody>
      </Table>
    </Wrapper>
  );
};
